"use client";

import { GlowingEffect } from "./ui/glowing-effect";
import { useEffect } from "react";

export function GlowingEffectDemo() {
  useEffect(() => {
    // Add keyframes to the document if they don't exist
    if (!document.querySelector('#fade-up-keyframes')) {
      const style = document.createElement('style');
      style.id = 'fade-up-keyframes';
      style.innerHTML = `
        @keyframes fadeUp {
          from {
            opacity: 0;
            transform: translateY(2rem);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-up {
          animation: fadeUp 0.6s ease-out;
          animation-play-state: paused;
        }
        .animate-fade-up.in-view {
          animation-play-state: running;
        }
      `;
      document.head.appendChild(style);
    }

    // Set up intersection observer
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '50px'
      }
    );

    // Observe all grid items
    document.querySelectorAll('.animate-fade-up').forEach((item) => {
      observer.observe(item);
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div id="how-it-works" className="py-16 relative z-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-2">
            <svg className="w-4 h-4 text-gray-300 filter drop-shadow-[0_0_2px_rgba(255,255,255,0.6)]" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 12L7 16L11 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M3 7L7 11L11 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M3 17L7 21L11 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M13 12H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M13 7H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M13 17H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <div className="text-sm text-gray-400 font-['VCR_OSD_Mono'] font-normal">STEPS</div>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-2">
            How it works
          </h2>
        </div>
        
        <div className="relative">
        <ul className="grid grid-cols-1 grid-rows-none gap-4 md:grid-cols-12 md:grid-rows-3 lg:gap-4 xl:max-h-[34rem] xl:grid-rows-2">
          <GridItem
            area="md:[grid-area:1/1/2/7] xl:[grid-area:1/1/2/5]"
            number="1"
            title="Sign Up & Connect"
            description="Create your account, discuss your integration needs, and we'll help connect your support channels."
          />

          <GridItem
            area="md:[grid-area:1/7/2/13] xl:[grid-area:2/1/3/5]"
            number="2"
            title="Configure Your Settings"
            description="Customize TiccAI to match your support workflow and preferences."
          />

          <GridItem
            area="md:[grid-area:2/1/3/7] xl:[grid-area:1/5/3/8]"
            number="3"
            title="Train the AI"
            description="Let TiccAI learn from your past tickets and documentation."
          />

          <GridItem
            area="md:[grid-area:2/7/3/13] xl:[grid-area:1/8/2/13]"
            number="4"
            title="Start Automating"
            description="Watch as TiccAI begins handling routine support tickets automatically."
          />

          <GridItem
            area="md:[grid-area:3/1/4/13] xl:[grid-area:2/8/3/13]"
            number="5"
            title="Monitor & Optimize"
            description="Track performance and continuously improve your support automation."
          />
        </ul>
        </div>
      </div>
    </div>
  );
}

interface GridItemProps {
  area: string;
  number: string;
  title: string;
  description: React.ReactNode;
}

const GridItem = ({ area, number, title, description }: GridItemProps) => {
  const progress = (parseInt(number) / 5) * 100;
  const delay = `${(parseInt(number) - 1) * 150}ms`;
  
  return (
    <li className={`min-h-[14rem] list-none ${area} opacity-0 translate-y-8 animate-fade-up group`}
        style={{ 
          animationDelay: delay,
          animationFillMode: 'forwards'
        }}>
      <div className="relative h-full rounded-2xl border border-white/10 p-2 md:rounded-3xl md:p-3 bg-gradient-to-br from-[#070d17]/95 via-[#0a1420]/95 to-[#070d17]/90 backdrop-blur-lg shadow-[0_2px_24px_0_rgba(9,16,28,0.25)] before:absolute before:inset-0 before:rounded-2xl before:bg-gradient-to-br before:from-white/0 before:to-white/0 before:content-[''] transition-all duration-500 hover:shadow-[0_0_40px_8px_rgba(168,85,247,0.12),0_2px_24px_0_rgba(9,16,28,0.25)] hover:border-pink-500/30 hover:scale-[1.025] hover:-translate-y-1">
        <GlowingEffect
          blur={0}
          borderWidth={3}
          spread={80}
          glow={true}
          disabled={false}
          proximity={64}
          inactiveZone={0.01}
        />
        <div className="border-0.75 relative flex h-full flex-col justify-between gap-6 overflow-hidden rounded-xl p-6 md:p-6 bg-gradient-to-br from-[#0a1420]/80 via-[#070d17]/90 to-[#0a1420]/80">
          {/* Background Illustrations */}
          <div className="absolute inset-0 opacity-[0.04] pointer-events-none">
            {number === "1" && (
              <svg width="100%" height="100%" viewBox="0 0 300 200" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Modern Dashboard Header */}
                <rect x="20" y="20" width="260" height="45" rx="8" fill="currentColor" className="text-gray-500"/>
                <circle cx="45" cy="42" r="12" fill="white" fillOpacity="0.05"/>
                <rect x="70" y="35" width="140" height="14" rx="2" fill="white" fillOpacity="0.05"/>
                <circle cx="255" cy="42" r="12" fill="currentColor" className="text-gray-500"/>
                <path d="M249 42L253 46L261 38" stroke="white" strokeWidth="2" strokeOpacity="0.05"/>
                
                {/* Integration Cards */}
                <g transform="translate(20, 80)">
                  {/* Card 1 */}
                  <rect x="0" y="0" width="80" height="80" rx="10" fill="currentColor" className="text-gray-500"/>
                  <rect x="10" y="10" width="60" height="8" rx="2" fill="white" fillOpacity="0.05"/>
                  <rect x="10" y="25" width="40" height="45" rx="4" fill="white" fillOpacity="0.05"/>
                  <circle cx="30" cy="45" r="8" fill="white" fillOpacity="0.05"/>
                  <path d="M25 45L30 50L35 40" stroke="white" strokeWidth="1.5" strokeOpacity="0.05"/>
                  
                  {/* Card 2 */}
                  <rect x="90" y="0" width="80" height="80" rx="10" fill="currentColor" className="text-gray-500"/>
                  <rect x="100" y="10" width="60" height="8" rx="2" fill="white" fillOpacity="0.05"/>
                  <rect x="100" y="25" width="40" height="45" rx="4" fill="white" fillOpacity="0.05"/>
                  <circle cx="130" cy="45" r="8" fill="white" fillOpacity="0.05"/>
                  <path d="M125 45L130 50L135 40" stroke="white" strokeWidth="1.5" strokeOpacity="0.05"/>
                  
                  {/* Card 3 */}
                  <rect x="180" y="0" width="80" height="80" rx="10" fill="currentColor" className="text-gray-500"/>
                  <rect x="190" y="10" width="60" height="8" rx="2" fill="white" fillOpacity="0.05"/>
                  <rect x="190" y="25" width="40" height="45" rx="4" fill="white" fillOpacity="0.05"/>
                  <circle cx="220" cy="45" r="8" fill="white" fillOpacity="0.05"/>
                  <path d="M215 45L220 50L225 40" stroke="white" strokeWidth="1.5" strokeOpacity="0.05"/>
                </g>
                
                {/* Connection Lines */}
                <path d="M100 120C100 135 150 135 150 120" stroke="currentColor" strokeWidth="2" className="text-gray-500" strokeDasharray="4 4" strokeOpacity="0.3"/>
                <path d="M200 120C200 135 150 135 150 120" stroke="currentColor" strokeWidth="2" className="text-gray-500" strokeDasharray="4 4" strokeOpacity="0.3"/>
                <path d="M60 120L80 120" stroke="currentColor" strokeWidth="2" className="text-gray-500" strokeOpacity="0.3"/>
                <path d="M220 120L240 120" stroke="currentColor" strokeWidth="2" className="text-gray-500" strokeOpacity="0.3"/>
              </svg>
            )}
            {number === "2" && (
              <svg width="100%" height="100%" viewBox="0 0 300 200" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Modern Settings Interface */}
                <g className="text-gray-500">
                  {/* Left Sidebar */}
                  <rect x="20" y="20" width="60" height="160" rx="10" fill="currentColor" fillOpacity="0.1"/>
                  <circle cx="50" cy="45" r="8" fill="white" fillOpacity="0.05"/>
                  <circle cx="50" cy="75" r="8" fill="white" fillOpacity="0.05"/>
                  <circle cx="50" cy="105" r="8" fill="white" fillOpacity="0.05"/>
                  <circle cx="50" cy="135" r="8" fill="white" fillOpacity="0.05"/>
                  
                  {/* Main Content */}
                  <rect x="90" y="20" width="190" height="160" rx="10" fill="currentColor" fillOpacity="0.1"/>
                
                {/* Header */}
                  <rect x="100" y="35" width="170" height="30" rx="6" fill="white" fillOpacity="0.05"/>
                  <rect x="110" y="43" width="120" height="14" rx="2" fill="white" fillOpacity="0.05"/>
                  <circle cx="250" cy="50" r="8" fill="white" fillOpacity="0.05"/>
                  
                  {/* Settings Sections */}
                  <g transform="translate(100, 75)">
                    {/* Section 1 */}
                    <rect x="0" y="0" width="170" height="25" rx="6" fill="white" fillOpacity="0.05"/>
                    <rect x="10" y="7" width="80" height="10" rx="2" fill="white" fillOpacity="0.05"/>
                    <rect x="120" y="5" width="40" height="15" rx="7" fill="white" fillOpacity="0.05"/>
                    <circle cx="140" cy="12" r="6" fill="white" fillOpacity="0.05"/>
                    
                    {/* Section 2 */}
                    <rect x="0" y="35" width="170" height="25" rx="6" fill="white" fillOpacity="0.05"/>
                    <rect x="10" y="42" width="90" height="10" rx="2" fill="white" fillOpacity="0.05"/>
                    <rect x="120" y="40" width="40" height="15" rx="7" fill="white" fillOpacity="0.05"/>
                    
                    {/* Section 3 */}
                    <rect x="0" y="70" width="170" height="25" rx="6" fill="white" fillOpacity="0.05"/>
                    <rect x="10" y="77" width="70" height="10" rx="2" fill="white" fillOpacity="0.05"/>
                    <rect x="120" y="75" width="40" height="15" rx="7" fill="white" fillOpacity="0.05"/>
                    <circle cx="130" cy="82" r="6" fill="white" fillOpacity="0.05"/>
                  </g>
                  
                  {/* Bottom Actions */}
                  <rect x="100" y="160" width="170" height="1" fill="white" fillOpacity="0.05"/>
                  <rect x="230" y="170" width="50" height="8" rx="4" fill="white" fillOpacity="0.05"/>
                </g>
              </svg>
            )}
            {number === "3" && (
              <svg width="100%" height="100%" viewBox="0 0 300 200" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Neural Network Visualization */}
                <g className="text-gray-500">
                  {/* Input Layer */}
                  <g transform="translate(30, 40)">
                    <circle cx="0" cy="0" r="8" fill="currentColor" fillOpacity="0.15"/>
                    <circle cx="0" cy="40" r="8" fill="currentColor" fillOpacity="0.15"/>
                    <circle cx="0" cy="80" r="8" fill="currentColor" fillOpacity="0.15"/>
                  </g>
                  
                  {/* Hidden Layer 1 */}
                  <g transform="translate(100, 20)">
                    <circle cx="0" cy="0" r="8" fill="currentColor" fillOpacity="0.15"/>
                    <circle cx="0" cy="40" r="8" fill="currentColor" fillOpacity="0.15"/>
                    <circle cx="0" cy="80" r="8" fill="currentColor" fillOpacity="0.15"/>
                    <circle cx="0" cy="120" r="8" fill="currentColor" fillOpacity="0.15"/>
                  </g>
                  
                  {/* Hidden Layer 2 */}
                  <g transform="translate(170, 40)">
                    <circle cx="0" cy="0" r="8" fill="currentColor" fillOpacity="0.15"/>
                    <circle cx="0" cy="40" r="8" fill="currentColor" fillOpacity="0.15"/>
                    <circle cx="0" cy="80" r="8" fill="currentColor" fillOpacity="0.15"/>
                  </g>
                  
                  {/* Output Layer */}
                  <g transform="translate(240, 60)">
                    <circle cx="0" cy="0" r="8" fill="currentColor" fillOpacity="0.15"/>
                    <circle cx="0" cy="40" r="8" fill="currentColor" fillOpacity="0.15"/>
                  </g>
                  
                  {/* Connections with Gradient */}
                  <defs>
                    <linearGradient id="connectionGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" style={{stopColor: 'currentColor', stopOpacity: 0.25}}/>
                      <stop offset="100%" style={{stopColor: 'currentColor', stopOpacity: 0.08}}/>
                    </linearGradient>
                    <filter id="glow">
                      <feGaussianBlur stdDeviation="1" result="coloredBlur"/>
                      <feMerge>
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="SourceGraphic"/>
                      </feMerge>
                    </filter>
                  </defs>
                  
                  {/* Layer 1 to 2 Connections */}
                  <path d="M38 40L92 20" stroke="url(#connectionGradient)" strokeWidth="1.5" filter="url(#glow)"/>
                  <path d="M38 40L92 60" stroke="url(#connectionGradient)" strokeWidth="1.5" filter="url(#glow)"/>
                  <path d="M38 80L92 60" stroke="url(#connectionGradient)" strokeWidth="1.5" filter="url(#glow)"/>
                  <path d="M38 80L92 100" stroke="url(#connectionGradient)" strokeWidth="1.5" filter="url(#glow)"/>
                  <path d="M38 120L92 100" stroke="url(#connectionGradient)" strokeWidth="1.5" filter="url(#glow)"/>
                  <path d="M38 120L92 140" stroke="url(#connectionGradient)" strokeWidth="1.5" filter="url(#glow)"/>
                  
                  {/* Layer 2 to 3 Connections */}
                  <path d="M108 20L162 40" stroke="url(#connectionGradient)" strokeWidth="1.5" filter="url(#glow)"/>
                  <path d="M108 60L162 40" stroke="url(#connectionGradient)" strokeWidth="1.5" filter="url(#glow)"/>
                  <path d="M108 60L162 80" stroke="url(#connectionGradient)" strokeWidth="1.5" filter="url(#glow)"/>
                  <path d="M108 100L162 80" stroke="url(#connectionGradient)" strokeWidth="1.5" filter="url(#glow)"/>
                  <path d="M108 100L162 120" stroke="url(#connectionGradient)" strokeWidth="1.5" filter="url(#glow)"/>
                  <path d="M108 140L162 120" stroke="url(#connectionGradient)" strokeWidth="1.5" filter="url(#glow)"/>
                  
                  {/* Layer 3 to 4 Connections */}
                  <path d="M178 40L232 60" stroke="url(#connectionGradient)" strokeWidth="1.5" filter="url(#glow)"/>
                  <path d="M178 80L232 60" stroke="url(#connectionGradient)" strokeWidth="1.5" filter="url(#glow)"/>
                  <path d="M178 80L232 100" stroke="url(#connectionGradient)" strokeWidth="1.5" filter="url(#glow)"/>
                  <path d="M178 120L232 100" stroke="url(#connectionGradient)" strokeWidth="1.5" filter="url(#glow)"/>
                  
                  {/* Active Nodes */}
                  <circle cx="30" cy="80" r="4" fill="white" fillOpacity="0.2"/>
                  <circle cx="100" cy="60" r="4" fill="white" fillOpacity="0.2"/>
                  <circle cx="170" cy="80" r="4" fill="white" fillOpacity="0.2"/>
                  <circle cx="240" cy="100" r="4" fill="white" fillOpacity="0.2"/>
                </g>
              </svg>
            )}
            {number === "4" && (
              <svg width="100%" height="100%" viewBox="0 0 300 200" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Workflow Automation Diagram */}
                <g className="text-gray-500">
                  {/* Input Box */}
                  <rect x="20" y="40" width="70" height="45" rx="8" fill="currentColor" fillOpacity="0.1"/>
                  <rect x="25" y="45" width="60" height="10" rx="2" fill="white" fillOpacity="0.05"/>
                  <rect x="25" y="60" width="50" height="10" rx="2" fill="white" fillOpacity="0.05"/>
                  
                  {/* Process Boxes */}
                  <g transform="translate(110, 30)">
                    <rect x="0" y="0" width="70" height="45" rx="8" fill="currentColor" fillOpacity="0.1"/>
                    <rect x="5" y="5" width="60" height="10" rx="2" fill="white" fillOpacity="0.05"/>
                    <rect x="5" y="20" width="50" height="10" rx="2" fill="white" fillOpacity="0.05"/>
                    <circle cx="60" cy="22" r="4" fill="white" fillOpacity="0.05"/>
                  </g>
                  
                  <g transform="translate(110, 90)">
                    <rect x="0" y="0" width="70" height="45" rx="8" fill="currentColor" fillOpacity="0.1"/>
                    <rect x="5" y="5" width="60" height="10" rx="2" fill="white" fillOpacity="0.05"/>
                    <rect x="5" y="20" width="50" height="10" rx="2" fill="white" fillOpacity="0.05"/>
                    <circle cx="60" cy="22" r="4" fill="white" fillOpacity="0.05"/>
                  </g>
                  
                  <g transform="translate(110, 150)">
                    <rect x="0" y="0" width="70" height="45" rx="8" fill="currentColor" fillOpacity="0.1"/>
                    <rect x="5" y="5" width="60" height="10" rx="2" fill="white" fillOpacity="0.05"/>
                    <rect x="5" y="20" width="50" height="10" rx="2" fill="white" fillOpacity="0.05"/>
                    <circle cx="60" cy="22" r="4" fill="white" fillOpacity="0.05"/>
                  </g>
                  
                  {/* Output Box */}
                  <rect x="210" y="90" width="70" height="45" rx="8" fill="currentColor" fillOpacity="0.1"/>
                  <rect x="215" y="95" width="60" height="10" rx="2" fill="white" fillOpacity="0.05"/>
                  <rect x="215" y="110" width="50" height="10" rx="2" fill="white" fillOpacity="0.05"/>
                  
                  {/* Connecting Lines with Gradient */}
                  <defs>
                    <linearGradient id="flowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" style={{stopColor: 'currentColor', stopOpacity: 0.2}}/>
                      <stop offset="100%" style={{stopColor: 'currentColor', stopOpacity: 0.05}}/>
                    </linearGradient>
                  </defs>
                  
                  <path d="M90 62L110 52" stroke="url(#flowGradient)" strokeWidth="2"/>
                  <path d="M90 62L110 112" stroke="url(#flowGradient)" strokeWidth="2"/>
                  <path d="M90 62L110 172" stroke="url(#flowGradient)" strokeWidth="2"/>
                  
                  <path d="M180 52L210 112" stroke="url(#flowGradient)" strokeWidth="2"/>
                  <path d="M180 112L210 112" stroke="url(#flowGradient)" strokeWidth="2"/>
                  <path d="M180 172L210 112" stroke="url(#flowGradient)" strokeWidth="2"/>
                  
                  {/* Flow Indicators */}
                  <circle cx="100" cy="57" r="3" fill="currentColor" fillOpacity="0.1"/>
                  <circle cx="190" cy="82" r="3" fill="currentColor" fillOpacity="0.1"/>
                  <circle cx="190" cy="112" r="3" fill="currentColor" fillOpacity="0.1"/>
                  <circle cx="190" cy="142" r="3" fill="currentColor" fillOpacity="0.1"/>
                </g>
              </svg>
            )}
            {number === "5" && (
              <div className="absolute top-2 right-2">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="transition-transform duration-500 group-hover:scale-110 group-hover:rotate-12">
                  <path d="M3 3V21H21" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M7 12L10 9L13 12L17 8" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="19" cy="5" r="2" fill="#22c55e"/>
                  <path d="M18 4L20 6" stroke="#22c55e" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M20 4L18 6" stroke="#22c55e" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              </div>
            )}
          </div>
          {/* Icons and Content */}
          {number === "1" && (
            <div className="absolute top-2 right-2">
              <svg width="24" height="24" viewBox="0 0 297 297" fill="#22c55e" xmlns="http://www.w3.org/2000/svg" className="transition-transform duration-500 group-hover:scale-110 group-hover:rotate-12">
                <path d="M286.942,155.775h-27.718c1.93-1.828,3.139-4.408,3.139-7.275v-0.502c0-5.54-4.492-10.031-10.032-10.031 c-5.54,0-10.032,4.491-10.032,10.031v0.502c0,2.867,1.209,5.447,3.138,7.275H148.5c-5.54,0-10.032,4.492-10.032,10.032v62.338 c-1.828-1.928-4.407-3.137-7.273-3.137h-0.501c-5.54,0-10.032,4.492-10.032,10.032c0,5.54,4.492,10.032,10.032,10.032h0.501 c2.866,0,5.445-1.209,7.273-3.136v45.03c0,5.54,4.492,10.032,10.032,10.032h138.442c5.54,0,10.032-4.492,10.032-10.032v-121.16 C296.975,160.268,292.482,155.775,286.942,155.775z M276.91,175.84v66.48H158.532v-66.48H276.91z M158.532,276.935v-14.551H276.91 v14.551H158.532z"/>
                <path d="M194.151,71.991h0.995c5.54,0,10.032-4.492,10.032-10.032c0-5.54-4.492-10.032-10.032-10.032h-0.995 c-5.54,0-10.031,4.492-10.031,10.032C184.12,67.499,188.611,71.991,194.151,71.991z"/>
                <path d="M222.991,71.991h0.994c5.54,0,10.032-4.492,10.032-10.032c0-5.54-4.492-10.032-10.032-10.032h-0.994 c-5.54,0-10.032,4.492-10.032,10.032C212.959,67.499,217.451,71.991,222.991,71.991z"/>
                <path d="M252.331,72.492c5.54,0,10.032-4.491,10.032-10.032v-0.501c0-5.54-4.492-10.032-10.032-10.032h-0.502 c-5.54,0-10.032,4.492-10.032,10.032c0,2.897,1.229,5.509,3.194,7.34C246.822,71.264,249.433,72.492,252.331,72.492z"/>
                <path d="M262.363,90.311c0-5.541-4.492-10.032-10.032-10.032c-5.54,0-10.032,4.491-10.032,10.032v0.994 c0,5.54,4.492,10.032,10.032,10.032c5.54,0,10.032-4.492,10.032-10.032V90.311z"/>
                <path d="M252.331,109.122c-5.54,0-10.032,4.492-10.032,10.032v0.994c0,5.54,4.492,10.032,10.032,10.032 c5.54,0,10.032-4.492,10.032-10.032v-0.994C262.363,113.614,257.871,109.122,252.331,109.122z"/>
                <path d="M102.85,225.009h-0.995c-5.54,0-10.031,4.492-10.031,10.032c0,5.54,4.491,10.032,10.031,10.032h0.995 c5.54,0,10.032-4.492,10.032-10.032C112.882,229.501,108.39,225.009,102.85,225.009z"/>
                <path d="M74.011,225.009h-0.994c-5.54,0-10.032,4.492-10.032,10.032c0,5.54,4.492,10.032,10.032,10.032h0.994 c5.54,0,10.032-4.492,10.032-10.032C84.043,229.501,79.551,225.009,74.011,225.009z"/>
                <path d="M44.671,224.508c-5.54,0-10.032,4.491-10.032,10.032v0.501c0,5.54,4.492,10.032,10.032,10.032h0.502 c5.54,0,10.032-4.492,10.032-10.032c0-2.897-1.229-5.509-3.194-7.34C50.18,225.736,47.569,224.508,44.671,224.508z"/>
                <path d="M54.703,176.852c0-5.54-4.492-10.032-10.032-10.032s-10.032,4.492-10.032,10.032v0.994c0,5.54,4.492,10.032,10.032,10.032 s10.032-4.492,10.032-10.032V176.852z"/>
                <path d="M44.671,195.663c-5.54,0-10.032,4.492-10.032,10.032v0.994c0,5.541,4.492,10.032,10.032,10.032s10.032-4.491,10.032-10.032 v-0.994C54.703,200.155,50.211,195.663,44.671,195.663z"/>
                <path d="M34.639,148.5v0.502c0,5.54,4.492,10.031,10.032,10.031s10.032-4.491,10.032-10.031V148.5c0-2.867-1.209-5.447-3.138-7.275 H148.5c5.54,0,10.032-4.492,10.032-10.032V68.854c1.828,1.928,4.407,3.137,7.273,3.137h0.501c5.54,0,10.032-4.492,10.032-10.032 c0-5.54-4.492-10.032-10.032-10.032h-0.501c-2.866,0-5.445,1.209-7.273,3.136v-45.03C158.532,4.492,154.04,0,148.5,0H10.058 C4.518,0,0.025,4.492,0.025,10.032v121.16c0,5.54,4.492,10.032,10.032,10.032h27.72C35.848,143.053,34.639,145.633,34.639,148.5z M138.468,20.065v66.479H20.09V20.065H138.468z M20.09,106.608h118.378v14.552H20.09V106.608z"/>
              </svg>
            </div>
          )}
          {number === "2" && (
            <div className="absolute top-2 right-2">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="transition-transform duration-500 group-hover:scale-110 group-hover:rotate-12">
                <path d="M10.2468 5.13331L4.1333 11.2469L7.66035 14.7739C9.34855 16.4621 12.0857 16.4621 13.7739 14.7739C15.4621 13.0857 15.4621 10.3486 13.7739 8.66036L10.2468 5.13331Z" stroke="#22c55e" strokeWidth="1.7" strokeLinecap="round"/>
                <path d="M5.21183 9.50372L3 7.29189" stroke="#22c55e" strokeWidth="1.7" strokeLinecap="round"/>
                <path d="M8.50382 6.21183L6.29199 4" stroke="#22c55e" strokeWidth="1.7" strokeLinecap="round"/>
                <path d="M13.7739 14.7739L15.1847 16.1847L20.0001 21" stroke="#22c55e" strokeWidth="1.7" strokeLinecap="round"/>
                <path d="M3.19287 12.1873L11.1875 4.1927" stroke="#22c55e" strokeWidth="1.7" strokeLinecap="round"/>
              </svg>
            </div>
          )}
          {number === "3" && (
            <div className="absolute top-2 right-2">
              <svg width="24" height="24" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="transition-transform duration-500 group-hover:scale-110 group-hover:rotate-12">
                <circle cx="34.52" cy="11.43" r="5.82" stroke="#22c55e" strokeWidth="3"/>
                <circle cx="53.63" cy="31.6" r="5.82" stroke="#22c55e" strokeWidth="3"/>
                <circle cx="34.52" cy="50.57" r="5.82" stroke="#22c55e" strokeWidth="3"/>
                <circle cx="15.16" cy="42.03" r="5.82" stroke="#22c55e" strokeWidth="3"/>
                <circle cx="15.16" cy="19.27" r="5.82" stroke="#22c55e" strokeWidth="3"/>
                <circle cx="34.51" cy="29.27" r="4.7" stroke="#22c55e" strokeWidth="3"/>
                <line x1="20.17" y1="16.3" x2="28.9" y2="12.93" stroke="#22c55e" strokeWidth="3"/>
                <line x1="38.6" y1="15.59" x2="49.48" y2="27.52" stroke="#22c55e" strokeWidth="3"/>
                <line x1="50.07" y1="36.2" x2="38.67" y2="46.49" stroke="#22c55e" strokeWidth="3"/>
                <line x1="18.36" y1="24.13" x2="30.91" y2="46.01" stroke="#22c55e" strokeWidth="3"/>
                <line x1="20.31" y1="44.74" x2="28.7" y2="48.63" stroke="#22c55e" strokeWidth="3"/>
                <line x1="17.34" y1="36.63" x2="31.37" y2="16.32" stroke="#22c55e" strokeWidth="3"/>
                <line x1="20.52" y1="21.55" x2="30.34" y2="27.1" stroke="#22c55e" strokeWidth="3"/>
                <line x1="39.22" y1="29.8" x2="47.81" y2="30.45" stroke="#22c55e" strokeWidth="3"/>
                <line x1="34.51" y1="33.98" x2="34.52" y2="44.74" stroke="#22c55e" strokeWidth="3"/>
              </svg>
            </div>
          )}
          {number === "4" && (
            <div className="absolute top-2 right-2">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="transition-transform duration-500 group-hover:scale-110 group-hover:rotate-12">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M15.0614 9.67972L16.4756 11.0939L17.8787 9.69083L16.4645 8.27662L15.0614 9.67972ZM16.4645 6.1553L20 9.69083L8.6863 21.0045L5.15076 17.469L16.4645 6.1553Z" fill="#22c55e"/>
                <path fill-rule="evenodd" clip-rule="evenodd" d="M11.364 5.06066L9.59619 6.82843L8.53553 5.76777L10.3033 4L11.364 5.06066ZM6.76778 6.82842L5 5.06067L6.06066 4L7.82843 5.76776L6.76778 6.82842ZM10.3033 10.364L8.53553 8.5962L9.59619 7.53554L11.364 9.3033L10.3033 10.364ZM7.82843 8.5962L6.06066 10.364L5 9.3033L6.76777 7.53554L7.82843 8.5962Z" fill="#22c55e"/>
              </svg>
            </div>
          )}
          {number === "5" && (
            <div className="absolute top-2 right-2">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="transition-transform duration-500 group-hover:scale-110 group-hover:rotate-12">
                <path d="M3 3V21H21" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M7 12L10 9L13 12L17 8" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="19" cy="5" r="2" fill="#22c55e"/>
                <path d="M18 4L20 6" stroke="#22c55e" strokeWidth="2" strokeLinecap="round"/>
                <path d="M20 4L18 6" stroke="#22c55e" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
          )}
          <div className="relative flex flex-1 flex-col justify-between gap-3">
            <div className="relative w-fit rounded-full p-3 bg-gradient-to-br from-pink-500/20 via-purple-500/20 to-blue-500/20 backdrop-blur-sm border border-white/10 shadow-[0_0_15px_rgba(236,72,153,0.3)] transition-all duration-500 group-hover:shadow-[0_0_20px_rgba(236,72,153,0.4)] group-hover:scale-110">
              <span className="relative z-10 text-2xl font-bold bg-gradient-to-br from-pink-500 via-purple-500 to-blue-500 bg-clip-text text-transparent">{number}</span>
              <div 
                className="absolute inset-0 rounded-full bg-gradient-to-br from-pink-500/30 via-purple-500/30 to-blue-500/30"
                style={{ 
                  clipPath: `polygon(0 0, ${progress}% 0, ${progress}% 100%, 0 100%)`,
                  transition: 'clip-path 0.3s ease'
                }}
              />
            </div>
            <div className="space-y-3">
              <h3 className="font-sans text-xl font-semibold text-white md:text-2xl transition-all duration-500 transform group-hover:scale-105 group-hover:-skew-x-3 group-hover:translate-x-2 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-pink-500 group-hover:to-purple-500">
                {title}
              </h3>
              <h2 className="font-sans text-sm text-gray-400 md:text-base transition-all duration-500 group-hover:text-gray-300">
                {description}
              </h2>
            </div>
          </div>
        </div>
      </div>
    </li>
  );
}; 